import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { UserCheck, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import ApprobationsCrudShell from './ApprobationsCrudShell';
import {
  ENTITY_TYPES, REQUEST_STATUS, PRIORITY_OPTIONS, statusBadge, entityLabel, defaultStats,
  mapRecordJsonFields, APPROBATIONS_WORKFLOWS_REFRESH_EVENT, buildActiveWorkflowOptions, parseApiRows,
} from './approbationsShared';
import { useApprobationsConfig } from './useApprobationsConfig';
import { resolveApprobationsEmployeeFromSelect } from './approbationsListPageUtils';

const NS = 'mesApprobationsPage';
const API = '/api/approbations/requests';
const WORKFLOWS_API = '/api/approbations/workflows';

export default function MesApprobationsPage() {
  const config = useApprobationsConfig();
  const [allWorkflows, setAllWorkflows] = useState([]);

  const emptyForm = useMemo(() => ({
    request_code: '', title: '', entity_type: '', entity_id: '', requester_id: '', requester_name: '',
    workflow_id: '', status: 'en_attente', priority: config.default_priority || 'normal', due_date: '', notes: '',
  }), [config.default_priority]);

  const loadWorkflows = useCallback(async () => {
    try {
      const response = await localApiAdapter.get(WORKFLOWS_API, { status: 'actif' });
      setAllWorkflows(parseApiRows(response));
    } catch {
      setAllWorkflows([]);
    }
  }, []);

  useEffect(() => {
    void loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    const handler = () => { void loadWorkflows(); };
    window.addEventListener(APPROBATIONS_WORKFLOWS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(APPROBATIONS_WORKFLOWS_REFRESH_EVENT, handler);
  }, [loadWorkflows]);

  const entityOpts = useMemo(
    () => ENTITY_TYPES.map((e) => ({ value: e.value, labelKey: e.labelKey })),
    [],
  );
  const statusOpts = useMemo(
    () => REQUEST_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey })),
    [],
  );
  const priorityOpts = useMemo(
    () => PRIORITY_OPTIONS.map((p) => ({ value: p.value, labelKey: p.labelKey })),
    [],
  );

  const formSections = useMemo(() => ([
    {
      id: 'identity',
      titleKey: 'section_identity',
      fields: [
        { key: 'title', labelKey: 'field_title', required: true },
        { key: 'entity_type', labelKey: 'field_entity_type', type: 'select', options: entityOpts },
        { key: 'entity_id', labelKey: 'field_entity_id' },
        { key: 'workflow_id', labelKey: 'field_workflow_id', type: 'select', options: [] },
        { key: 'requester_id', labelKey: 'field_requester', type: 'employee' },
      ],
    },
    {
      id: 'workflow',
      titleKey: 'section_workflow',
      fields: [
        { key: 'status', labelKey: 'field_status', type: 'select', options: statusOpts },
        { key: 'priority', labelKey: 'field_priority', type: 'select', options: priorityOpts },
        { key: 'due_date', labelKey: 'field_due_date', type: 'date' },
        { key: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true },
      ],
    },
  ]), [entityOpts, statusOpts, priorityOpts]);

  const resolveFieldOptions = useCallback((field, formData) => {
    if (field.key === 'workflow_id') {
      return buildActiveWorkflowOptions(allWorkflows, formData.entity_type || null);
    }
    return null;
  }, [allWorkflows]);

  const onFormFieldChange = useCallback((key, value, prev, employeeOptions = []) => {
    if (key === 'entity_type') {
      const next = { ...prev, entity_type: value };
      if (prev.workflow_id) {
        const wf = allWorkflows.find((w) => String(w.id) === String(prev.workflow_id));
        if (wf?.entity_type && wf.entity_type !== value) {
          next.workflow_id = '';
        }
      }
      return next;
    }
    if (key === 'requester_id') {
      const { employeeId, employeeName } = resolveApprobationsEmployeeFromSelect(value, employeeOptions);
      return {
        ...prev,
        requester_id: employeeId != null ? String(employeeId) : '',
        requester_name: employeeName,
      };
    }
    return { ...prev, [key]: value };
  }, [allWorkflows]);

  return (
    <ApprobationsCrudShell
      ns={NS}
      icon={UserCheck}
      apiPath={API}
      codeField="request_code"
      entityPrefixKey="approvalRequest"
      codePrefixFallback="AR"
      gradient="from-violet-500 to-violet-700"
      entityLabel="طلب الموافقة"
      entityLabelPlural="طلبات الموافقة"
      emptyForm={emptyForm}
      onModalOpen={loadWorkflows}
      resolveFieldOptions={resolveFieldOptions}
      onFormFieldChange={(key, value, prev, employeeOptions) => onFormFieldChange(key, value, prev, employeeOptions)}
      mapRecordToForm={(r) => ({
        ...mapRecordJsonFields(r, emptyForm),
        workflow_id: r.workflow_id != null ? String(r.workflow_id) : '',
        requester_id: r.requester_id != null ? String(r.requester_id) : '',
        requester_name: r.requester_name || '',
      })}
      computeStats={(records) => defaultStats(records, {
        totalIcon: UserCheck,
        fields: [
          { titleKey: 'stat_pending', field: 'status', value: 'en_attente', icon: Clock, color: 'from-amber-500 to-amber-600' },
          { titleKey: 'stat_in_progress', field: 'status', value: 'en_cours', icon: AlertCircle, color: 'from-blue-500 to-blue-600' },
          { titleKey: 'stat_approved', field: 'status', value: 'approuve', icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_rejected', field: 'status', value: 'rejete', icon: XCircle, color: 'from-red-500 to-red-600' },
        ],
      })}
      columns={[
        { key: 'request_code', labelKey: 'col_code' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'entity_type', labelKey: 'col_entity', render: (r, _p, _t, pl) => entityLabel(r.entity_type, pl) },
        { key: 'priority', labelKey: 'col_priority' },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl) },
        { key: 'due_date', labelKey: 'col_due_date' },
      ]}
      formSections={formSections}
      mapFormToPayload={(f) => ({
        request_code: f.request_code,
        title: f.title,
        entity_type: f.entity_type || null,
        entity_id: f.entity_id || null,
        workflow_id: f.workflow_id !== '' && f.workflow_id != null ? parseInt(f.workflow_id, 10) : null,
        requester_id: f.requester_id || null,
        requester_name: f.requester_name || null,
        status: f.status || 'en_attente',
        priority: f.priority || config.default_priority || 'normal',
        due_date: f.due_date || null,
        notes: f.notes || null,
      })}
    />
  );
}
